import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Briefcase, Building2, ChevronDown, ClipboardList, SlidersHorizontal, User, X } from 'lucide-react';
import { FilterDropdown, type FilterOptionItem } from '@/shared/ui';
import { getCompanies, isSuperAdmin } from '@/services/authService';
import type { FollowupState, ProcurementOrderRow, Urgency } from '@contracts/procurement';
import { useProcurementFilters } from '@/features/procurement/procurementContext';
import { useProcurementOrders } from '@/features/procurement/hooks/useProcurement';
import { ProcurementDateFilter } from './ProcurementDateFilter';
import { STATE_LABEL, URGENCY_LABEL } from './procurementMeta';

const Divider = () => <span className="mx-1 h-7 w-px self-center bg-slate-200" />;

/** Compras vive en Odoo; SmartBamboo (Metabase) queda fuera del MVP. */
const ODOO_CATALOG: FilterOptionItem[] = [
  { id: 'vg', name: 'Vine Garden' },
  { id: 'pi', name: 'Pielé' },
  // La matriz compra sin tener POS (sin pos_slug) — bucket 'corp' del BFF.
  { id: 'corp', name: 'Corporativo (S&G)' },
];

const STATE_OPTIONS: FilterOptionItem[] = (
  ['nueva', 'autorizada', 'en_compra', 'cancelada'] as FollowupState[]
).map((s) => ({ id: s, name: STATE_LABEL[s] }));

const URGENCY_OPTIONS: FilterOptionItem[] = [
  { id: 'urgent', name: URGENCY_LABEL.urgent },
  { id: 'normal', name: URGENCY_LABEL.normal },
];

/** Opciones únicas {id, name} de un m2o presente en las filas cargadas. */
function uniqueOptions(
  rows: ProcurementOrderRow[],
  id: (r: ProcurementOrderRow) => number | undefined,
  name: (r: ProcurementOrderRow) => string | undefined,
): FilterOptionItem[] {
  const seen = new Map<number, string>();
  for (const r of rows) {
    const key = id(r);
    const label = name(r);
    if (key != null && label && !seen.has(key)) seen.set(key, label);
  }
  return [...seen.entries()]
    .map(([oid, oname]) => ({ id: oid, name: oname }))
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

/**
 * Barra de filtros del dominio Compras — mismo lenguaje visual que el
 * FilterBar de ventas (barra blanca, divisores, colapso móvil con badge),
 * pero desacoplada: vive en el slice y usa el FilterDropdown genérico.
 */
export function ProcurementFilterBar() {
  const { period, companies, scope, setCompanies, setScope, setPeriod, patchScope } =
    useProcurementFilters();
  const ordersQ = useProcurementOrders();
  const [open, setOpen] = useState(false);

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const rows = ordersQ.data?.data ?? [];
  const departments = useMemo(
    () => uniqueOptions(rows, (o) => o.departmentId, (o) => o.department),
    [rows],
  );
  const requesters = useMemo(
    () => uniqueOptions(rows, (o) => o.requesterId, (o) => o.requester),
    [rows],
  );

  const visibleCompanies = isSuperAdmin()
    ? ODOO_CATALOG
    : ODOO_CATALOG.filter(
        (c) => c.id === 'corp' || getCompanies().some((u) => u.slug === c.id),
      );
  const showCompany = visibleCompanies.length > 1;

  const activeScope = [
    scope.states,
    scope.departments,
    scope.urgency ? [scope.urgency] : undefined,
    scope.requesterId != null ? [scope.requesterId] : undefined,
    scope.slaState ? [scope.slaState] : undefined,
  ].filter((a) => a?.length).length;
  const anyActive = activeScope > 0 || period != null;

  const clearAll = () => {
    setScope({});
    setPeriod(null);
  };

  const scopeFilters = (fullWidth: boolean) => (
    <>
      <FilterDropdown
        fullWidth={fullWidth}
        label="Estatus"
        icon={ClipboardList}
        options={STATE_OPTIONS}
        selected={scope.states ?? []}
        onChange={(ids) => patchScope({ states: ids.length ? (ids as FollowupState[]) : undefined })}
      />
      <FilterDropdown
        fullWidth={fullWidth}
        label="Departamento"
        icon={Briefcase}
        options={departments}
        selected={scope.departments ?? []}
        onChange={(ids) => patchScope({ departments: ids.length ? ids : undefined })}
      />
      <FilterDropdown
        fullWidth={fullWidth}
        label="Solicitante"
        icon={User}
        searchable
        multiple={false}
        options={requesters}
        selected={scope.requesterId != null ? [scope.requesterId] : []}
        onChange={(ids) => patchScope({ requesterId: ids.length ? Number(ids[0]) : undefined })}
      />
      <FilterDropdown
        fullWidth={fullWidth}
        label="Urgencia"
        icon={AlertTriangle}
        multiple={false}
        options={URGENCY_OPTIONS}
        selected={scope.urgency ? [scope.urgency] : []}
        onChange={(ids) => patchScope({ urgency: ids.length ? (ids[0] as Urgency) : undefined })}
      />
    </>
  );

  const clearButton = anyActive ? (
    <button
      onClick={clearAll}
      className="flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-red-400 hover:text-red-600"
    >
      <X className="h-3.5 w-3.5" /> Limpiar
    </button>
  ) : null;

  const phaseChip = (
    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500">
      Gastos por depto.: fase 3
    </span>
  );

  // ── Móvil: período arriba, resto bajo "Filtros" ──────────────────────────
  if (isMobile) {
    return (
      <div className="border-b border-slate-200 bg-white px-4 py-2.5">
        <div className="flex items-center gap-2">
          <ProcurementDateFilter />
          <button
            onClick={() => setOpen((o) => !o)}
            className="ml-auto flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:border-slate-400"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filtros
            {activeScope > 0 && (
              <span className="grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-red-600 px-1 text-[11px] font-bold text-white">
                {activeScope}
              </span>
            )}
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {open && (
          <div className="mt-3 grid grid-cols-2 gap-2">
            {showCompany && (
              <div className="col-span-2">
                <FilterDropdown
                  fullWidth
                  label="Compañía"
                  icon={Building2}
                  requireOne
                  options={visibleCompanies}
                  selected={companies}
                  onChange={(ids) => setCompanies(ids as string[])}
                />
              </div>
            )}
            {scopeFilters(true)}
            <div className="col-span-2 flex items-center justify-between">
              {phaseChip}
              {clearButton}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Desktop: una fila con divisores (patrón del FilterBar de ventas) ──────
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-6 py-3">
      <ProcurementDateFilter />
      {showCompany && (
        <>
          <Divider />
          <FilterDropdown
            label="Compañía"
            icon={Building2}
            requireOne
            options={visibleCompanies}
            selected={companies}
            onChange={(ids) => setCompanies(ids as string[])}
          />
        </>
      )}
      <Divider />
      {scopeFilters(false)}
      {clearButton}
      <span className="ml-auto">{phaseChip}</span>
    </div>
  );
}
